import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Box, Button, CircularProgress, Drawer, LinearProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import dayjs from 'dayjs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { AuthStoreContext } from '../Root';
import Header from '../Header';
import { SideBarState } from '../SideBar';
import { readSelectionCookie } from '../dashboard/selection-cookie';
import { Selection } from '../dashboard/selection';
import { IntervalState } from '../time-interval-utils';
import { Filter, MeasureService } from '../../http/meas-service';
import FilterLayout from './filters/FilterLayout';
import TimeFilter, { TimeFilterState } from './filters/TimeFilter';
import { PaginationMeasStore } from '../../stores/pagination-meas-store';
import NormalTable from './tables/NormalTable';
import DetailedTable from './tables/DetailedTable';
import { TableMode, readTableModeCookie, writeTableModeCookie } from './table-mode';
import { buildCsv, buildFilename, downloadCsv } from './csv-export';

const DRAWER_WIDTH = 300;

interface RouterState {
    selection?: Selection;
    from?: string;
    to?: string;
}

function nodeIdFromSelection(sel: Selection, fallback: number): number {
    // prettier-ignore
    switch (sel.kind) {
        case 'node'    :
        case 'location':
        case 'device'  : return sel.nodeId;
        case 'kosa'    : return fallback;
    }
}

interface InitIds {
    locationIds: number[];
    deviceIds: number[];
    snsIds: number[];
}

function initIdsFromSelection(sel: Selection): InitIds {
    // prettier-ignore
    switch (sel.kind) {
        case 'location': return { locationIds: [sel.locationId], deviceIds: [],             snsIds: []           };
        case 'device':   return { locationIds: [],               deviceIds: [sel.deviceId], snsIds: []           };
        case 'kosa':     return { locationIds: [],               deviceIds: [],             snsIds: [sel.kosaId] };
        default:         return { locationIds: [],               deviceIds: [],             snsIds: []           };
    }
}

//=================================================================================================
const ExportConfigurator = observer(function ExportConfigurator() {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user!;

    const location = useLocation();
    const routerState = (location.state ?? null) as RouterState | null;

    const initSelection: Selection = routerState?.selection ??
        readSelectionCookie() ?? { kind: 'node', nodeId: user.node_id };
    const initNodeId = nodeIdFromSelection(initSelection, user.node_id);
    const initIds = initIdsFromSelection(initSelection);
    const initInterval: IntervalState | undefined =
        routerState?.from && routerState?.to ? { from: dayjs(routerState.from), to: dayjs(routerState.to) } : undefined;

    const [sideBar, setSideBar] = useState(SideBarState.open);
    const [measStore] = useState(() => new PaginationMeasStore());
    const [tableMode, setTableMode] = useState<TableMode>(readTableModeCookie);
    const [exporting, setExporting] = useState(false);

    const [timeState, setTimeState] = useState<TimeFilterState>(() =>
        initInterval ? { from: initInterval.from, to: initInterval.to } : { from: null, to: null },
    );

    const timeRef = useRef(timeState);
    // prettier-ignore
    const nonTimeRef = useRef<Omit<Filter, 'fr' | 'to'>>({
        node: initNodeId,
        lo:  initIds.locationIds.length > 0 ? initIds.locationIds : undefined,
        dev: initIds.deviceIds.length   > 0 ? initIds.deviceIds   : undefined,
        sns: initIds.snsIds.length      > 0 ? initIds.snsIds      : undefined,
    });

    const applyFilter = useCallback(
        (partial: Omit<Filter, 'fr' | 'to'>, time: TimeFilterState) => {
            measStore.setFilter({ ...partial, fr: time.from?.toDate(), to: time.to?.toDate() }).catch(console.log);
        },
        [measStore],
    );

    useEffect(() => {
        applyFilter(nonTimeRef.current, timeRef.current);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTimeChange = (state: TimeFilterState) => {
        setTimeState(state);
        timeRef.current = state;
        applyFilter(nonTimeRef.current, state);
    };

    const handleFilterChange = useCallback(
        (partial: Omit<Filter, 'fr' | 'to'>) => {
            nonTimeRef.current = partial;
            applyFilter(partial, timeRef.current);
        },
        [applyFilter],
    );

    const handleModeChange = (_: React.MouseEvent, value: TableMode | null) => {
        if (!value) return;
        setTableMode(value);
        writeTableModeCookie(value);
    };

    const handleExport = async () => {
        if (measStore.size === 0 || exporting) return;
        setExporting(true);
        try {
            const { data } = await MeasureService.getMeasures(measStore.filter, measStore.sorting, {
                offset: 0,
                limit: measStore.size,
            });
            downloadCsv(buildCsv(data, tableMode), buildFilename(tableMode));
        } catch (e) {
            console.error(e);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Header sideBar={{ state: sideBar, setState: setSideBar }} />
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Button
                    variant="text"
                    size="small"
                    startIcon={<ArrowBackIcon sx={{ width: 14, height: 14 }} />}
                    href="/"
                    sx={{ textTransform: 'none' }}
                >
                    Назад к измерениям
                </Button>
            </Box>
            <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
                <Box
                    sx={(theme) => ({
                        flexShrink: 0,
                        overflow: 'hidden',
                        bgcolor: 'sidebar.main',
                        width: sideBar === SideBarState.open ? DRAWER_WIDTH : 0,
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    })}
                >
                    <Drawer
                        variant="permanent"
                        sx={{
                            height: '100%',
                            '& .MuiDrawer-paper': {
                                position: 'relative',
                                width: DRAWER_WIDTH,
                                height: '100%',
                                overflowY: 'auto',
                                backgroundColor: 'sidebar.main',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                            },
                        }}
                    >
                        <FilterLayout
                            initialNodeId={initNodeId}
                            initialLocationIds={initIds.locationIds}
                            initialDeviceIds={initIds.deviceIds}
                            initialSnsIds={initIds.snsIds}
                            onChange={handleFilterChange}
                        />
                    </Drawer>
                </Box>

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'background.default',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                        <Box sx={{ flex: 1 }}>
                            <TimeFilter state={timeState} onChange={handleTimeChange} />
                        </Box>
                        <ToggleButtonGroup value={tableMode} exclusive onChange={handleModeChange} size="small">
                            <ToggleButton
                                value={TableMode.Normal}
                                sx={{ px: 1.5, py: 0.25, textTransform: 'none', fontWeight: 400, lineHeight: 1.5 }}
                            >
                                Сводная
                            </ToggleButton>
                            <ToggleButton
                                value={TableMode.Detailed}
                                sx={{ px: 1.5, py: 0.25, textTransform: 'none', fontWeight: 400, lineHeight: 1.5 }}
                            >
                                Детальная
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleExport}
                            disabled={exporting || measStore.size === 0}
                            startIcon={
                                exporting ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : (
                                    <FileDownloadIcon sx={{ fontSize: 16 }} />
                                )
                            }
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap', ml: 3 }}
                        >
                            Экспорт в CSV
                        </Button>
                    </Box>
                    <LinearProgress sx={{ visibility: measStore.loading ? 'visible' : 'hidden' }} />
                    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                        {tableMode === TableMode.Normal ? (
                            <NormalTable store={measStore} />
                        ) : (
                            <DetailedTable store={measStore} />
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
});

export default ExportConfigurator;
